import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, computed, Signal, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { first } from 'rxjs/operators';
import { RestoreIbbModelForStepHelpers } from 'wizards/helpers/restore-ibb-helpers/restore-ibb-model-for-step-helpers';
import { RestoreIbbPlanFromStepsHelpers } from 'wizards/helpers/restore-ibb-helpers/restore-ibb-plan-from-steps-helpers';
import { DeletePlan } from 'wizards/models/base/base-models/plan-restore-delete-plan-model';
import { CreateRVPlanParams } from 'wizards/models/base/base-plan-models';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { BackupToRestoreStepComponent } from 'wizards/steps/backup-to-restore-step/backup-to-restore-step.component';
import { DestinationStepComponent } from 'wizards/steps/destination-step/destination-step.component';
import { EncryptionOptionsStepComponent } from 'wizards/steps/encription-options-step/encryption-options-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { AzureInstanceStepComponent } from 'wizards/steps/only-restore-steps/azure-instance-step/azure-instance-step.component';
import { EC2InstanceStepComponent } from 'wizards/steps/only-restore-steps/ec2-instance-step/ec2-instance-step.component';
import { PartitionsStepComponent } from 'wizards/steps/only-restore-steps/partitions-step/partitions-step.component';
import { TypeDataStepComponent } from 'wizards/steps/only-restore-steps/type-data-step/type-data-step.component';
import { VmSettingsStepComponent } from 'wizards/steps/only-restore-steps/vm-settings-step/vm-settings-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { RestorePointStepComponent } from 'wizards/steps/restore-point-step/restore-point-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SelectComputerStepComponent } from 'wizards/steps/select-computer-step/select-computer-step.component';
import { SelectHostStepComponent } from 'wizards/steps/select-host-step/select-host-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';
import { PlanResponseModel } from '../../models/base/plan-response-model';
import { RestoreIbbFormModel } from '../../models/restoreIBB/restore-ibb-form-model';
import {
  CloudRestoreTypes,
  InstantRestoreTypes,
  RestoreAsDiskEnum,
  RestoreToHyperVTypes,
  RestoreToVMNeedHostTypes
} from '../../models/type-data-models';
import { WizardBaseClass } from '../wizard-base.class';

type ImageBasedRestoreWizardHelpDocumentationLinks =
  | 'welcome'
  | 'storage'
  | 'selectComputer'
  | 'selectBackup'
  | 'selectPoint'
  | 'typeOfData'
  | 'partitions'
  | 'host'
  | 'vmSettingsHyperV'
  | 'vmSettingsProxmox'
  | 'vmSettingsHyperVInstant'
  | 'vmSettingsProxmoxInstant'
  | 'vmSettingsVMware'
  | 'destination'
  | 'encryption'
  | 'schedule'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-image-based-restore-wizard',
  templateUrl: './image-based-restore-wizard.component.html',
  imports: [
    AsyncPipe,
    BackupToRestoreStepComponent,
    CommonModule,
    DestinationStepComponent,
    EncryptionOptionsStepComponent,
    FormsModule,
    NotificationsStepComponent,
    PartitionsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RestorePointStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectComputerStepComponent,
    TypeDataStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe,
    SelectHostStepComponent,
    VmSettingsStepComponent,
    EC2InstanceStepComponent,
    AzureInstanceStepComponent
  ]
})
export class ImageBasedRestoreWizardComponent extends WizardBaseClass {
  public readonly hostTitle = this.i18nPipe.transform('wizards:select_host_step_title');
  public readonly vmWareSettingsTitle = this.i18nPipe.transform('wizards:vmWareSettingsTitle');
  public readonly vmHyperVSettingsTitle = this.i18nPipe.transform('wizards:vmHyperVSettingsTitle');
  public readonly vmSettingsProxmoxTitle = this.i18nPipe.transform('wizards:vmSettingsProxmoxTitle');
  public readonly vmSettingsDRTitle = this.i18nPipe.transform('wizards:vmSettingsDRTitle');
  public readonly restoreAsDiskEnum = RestoreAsDiskEnum;
  private readonly cloudRestoreTypes: RestoreAsDiskEnum[] = CloudRestoreTypes;
  private readonly showCredentialsStepTypes: RestoreAsDiskEnum[] = RestoreToVMNeedHostTypes;
  private readonly hyperVTypes: RestoreAsDiskEnum[] = RestoreToHyperVTypes;

  public helpLink: WizardHelpLink<ImageBasedRestoreWizardHelpDocumentationLinks>;
  public restorePointTitle = this.i18nPipe.transform('wizards:restore_point_title');
  public partitionsTitle = this.i18nPipe.transform('wizards:partitions_step_title');

  public restoreAsValue: WritableSignal<RestoreAsDiskEnum> = signal(null);
  public showRestoreToCloudSteps: WritableSignal<boolean> = signal(false);
  public showSelectHostStep: WritableSignal<boolean> = signal(false);
  public showVmSettingsStep: WritableSignal<boolean> = signal(false);
  public isInstantRestore: WritableSignal<boolean> = signal(false);

  public isEC2: Signal<boolean> = computed(() => this.restoreAsValue() === RestoreAsDiskEnum.EC2);
  public helpLinkForSettingsStep: Signal<string> = computed(() => {
    const restoreAs = this.restoreAsValue();

    if (restoreAs === RestoreAsDiskEnum.InstantRestoreToHyperV) return this.helpLink.links.vmSettingsHyperVInstant;
    if (restoreAs === RestoreAsDiskEnum.InstantRestoreToProxmox) return this.helpLink.links.vmSettingsProxmoxInstant;
    // No dedicated article for the DR Server yet; the instant Hyper-V one describes the same layout
    if (restoreAs === RestoreAsDiskEnum.InstantRestoreToDRServer) return this.helpLink.links.vmSettingsHyperVInstant;

    return this.hyperVTypes.includes(restoreAs)
      ? this.helpLink.links.vmSettingsHyperV
      : restoreAs === RestoreAsDiskEnum.RestoreToProxmox
        ? this.helpLink.links.vmSettingsProxmox
        : this.helpLink.links.vmSettingsVMware;
  });

  constructor() {
    super();

    this.FormModel = new RestoreIbbFormModel(this.isNBF);
    if (!this.isRDMode) {
      this.myPlan$
        .pipe(
          first((p) => !!p),
          untilDestroyed(this)
        )
        .subscribe((plan) => plan && this.updateStepData(plan));
      return;
    }
    this.updateStepData(new PlanResponseModel(), true);
  }

  updateStepData(plan, isDefault = false): void {
    this.loadedPlan = plan;
    if (this.isEdit && !this.isRDMode) this.mainService.GetPlanNotificationOptions();

    if (isDefault) return this.setDefaultValue();

    this.FormModel = RestoreIbbModelForStepHelpers.RestoreIBBModelFromPlan(plan, this.defaultSettings());

    this.stepsManager.showEncryptionOptionsStepIfNeed();

    if (!this.isEdit && this.mainService.initialFormData) {
      this.storageManager.changeStorageHandler(this.FormModel.whereBackupStep.ConnectionID);
    }
  }

  restoreAsChangeHandler(event: RestoreAsDiskEnum): void {
    this.storageManager.setRestoreAs(event);
    this.restoreAsValue.set(event);

    const showSelectHostStep = this.showCredentialsStepTypes.includes(event);

    this.showSelectHostStep.set(showSelectHostStep);
    this.showRestoreToCloudSteps.set(this.cloudRestoreTypes.includes(event));
    this.showVmSettingsStep.set(
      showSelectHostStep || this.hyperVTypes.includes(event) || event === RestoreAsDiskEnum.InstantRestoreToDRServer
    );

    this.isInstantRestore.set(InstantRestoreTypes.includes(event));
  }

  handleSave(): void {
    if (!this.isRDMode && InstantRestoreTypes.includes(this.FormModel?.typeDataStep?.restoreAs)) return this.saveInstantlyPlan();

    super.handleSave();
  }

  protected getPlanForSaving(): any {
    return RestoreIbbPlanFromStepsHelpers.getUpdatedPlan(this.loadedPlan.Plan, this.FormModel, this.defaultSettings());
  }

  protected loadInfoHandlerFinally() {
    this.cdr.detectChanges();
  }

  getRAWHelpLinks(): Record<ImageBasedRestoreWizardHelpDocumentationLinks, string> {
    return {
      welcome: 'restore/ibb/console#planname',
      storage: 'restore/ibb/console#storage',
      selectComputer: 'restore/ibb/console#comp',
      selectBackup: 'restore/ibb/console#backup',
      selectPoint: `restore/ibb/console#rp`,
      typeOfData: `restore/ibb/console#restoreas`,
      partitions: `restore/ibb/console#partitions`,
      host: 'restore/vmware/restore-console#host',
      vmSettingsHyperV: 'm0801',
      vmSettingsProxmox: 'm0802',
      vmSettingsVMware: 'm0803',
      vmSettingsProxmoxInstant: 'm0804',
      vmSettingsHyperVInstant: 'm0805',
      destination: `restore/ibb/console#destination`,
      encryption: `restore/ibb/console#encryption`,
      schedule: `restore/ibb/console#schedule`,
      prePost: `restore/ibb/console#prepost`,
      notification: `restore/ibb/console#notification`
    };
  }

  private saveInstantlyPlan(): void {
    if (this.wizardFinish) return;

    const plan: CreateRVPlanParams = {
      path: this.FormModel.restorePointStep?.resultPoint?.path,
      password: this.FormModel.encryptionOptionsStep?.password || '',
      planName: this.FormModel.planNameStep?.Name || '',
      sourceHid: this.mainService.hid,
      destinationHid: this.mainService.targetComputerData?.hid || this.mainService.hid,
      externalUserId: this.mainService.userId,
      runAfterSave: !this.FormModel.planNameStep.SavePlanInCloud,
      interactiveMode: true,
      deletePlan:
        this.FormModel?.typeDataStep?.restoreAs === RestoreAsDiskEnum.InstantRestoreToDRServer ? DeletePlan.Never : DeletePlan.OnCompletes,
      vmSettings: {
        VmName: this.FormModel?.vmSettingsStep?.vmName,
        isInstantRestore: true,
        numCpu: this.FormModel?.vmSettingsStep?.numCPU,
        memoryInMb: this.FormModel?.vmSettingsStep?.memorySize,
        testType: this.instantRestoreTestType()
      }
    };

    if (this.FormModel?.typeDataStep?.restoreAs === RestoreAsDiskEnum.InstantRestoreToProxmox) {
      plan.vmSettings.vmUuid = this.FormModel.vmSettingsStep.vmId;
      plan.vmSettings.host = this.FormModel.selectHostStep.server;
      plan.vmSettings.node = this.FormModel.selectHostStep.host;
      plan.vmSettings.storage = this.FormModel.vmSettingsStep.datastore;
      plan.vmSettings.guestOsType = this.FormModel.vmSettingsStep.guestOs;
      plan.vmSettings.overwrite = this.FormModel.vmSettingsStep.overwrite;
      plan.vmSettings.powerOnAfterRestore = false;
      plan.vmSettings.network = {
        bridge: this.FormModel.vmSettingsStep.virtualNetwork,
        model: this.FormModel.vmSettingsStep.cardModel
      };
      plan.vmSettings.loginPassword = {
        login: this.FormModel.selectHostStep.login,
        password: this.FormModel.selectHostStep.password
      };
    } else {
      plan.vmSettings.network = this.FormModel.vmSettingsStep.virtualNetwork;
      plan.vmSettings.overwrite = this.FormModel?.vmSettingsStep?.overwrite;
      plan.vmSettings.powerOnAfterRestore = false;

      // A DR Server offers neither of these, so the step hides them and the plan must not carry stale defaults
      if (this.FormModel?.typeDataStep?.restoreAs !== RestoreAsDiskEnum.InstantRestoreToDRServer) {
        plan.vmSettings.dynamicMemoryEnabled = this.FormModel?.vmSettingsStep?.dynamic;
        plan.vmSettings.minMemoryInMb = this.FormModel?.vmSettingsStep?.minMemorySize;
        plan.vmSettings.maxMemoryInMb = this.FormModel?.vmSettingsStep?.maxMemorySize;
        plan.destinationFolder = this.FormModel?.vmSettingsStep?.destination;
      } else plan.vmSettings.machineHid = this.mainService.hid;
    }

    this.mainService
      .createRestoreVerificationPlan(plan)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: () => {
          this.stopLoadingAndNotFinish();
          this.wizardFinish = true;
        },
        error: () => this.stopLoadingAndNotFinish()
      });
  }

  private instantRestoreTestType(): 'LocalHyperV' | 'Proxmox' | 'MSP360LDR' {
    const restoreAs = this.FormModel?.typeDataStep?.restoreAs;

    if (restoreAs === RestoreAsDiskEnum.InstantRestoreToDRServer) return 'MSP360LDR';

    return restoreAs === RestoreAsDiskEnum.InstantRestoreToHyperV ? 'LocalHyperV' : 'Proxmox';
  }

  private stopLoadingAndNotFinish(): void {
    this.wizard.spinnerShow = false;
    this.loadingPlan = false;
  }
}
